import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Button, Statistic, Row, Col, message, Empty, Modal, InputNumber, Space, Alert } from 'antd';
import { ReloadOutlined, WalletOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { userPath } from '../../../shared/config/paths';
import billingService from '../../services/billingService';
import PaymentMethods from '../../components/PaymentMethods';
import moment from 'moment';
import { formatMoney, currencySymbol, PLATFORM_CURRENCY } from '../../../shared/utils/money';
import BalanceMoney from '../../../shared/components/BalanceMoney';

const TOPUP_PRESETS = [50, 100, 200, 500, 1000];

const Balance = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [balance, setBalance] = useState(0);
    const [balanceCurrency, setBalanceCurrency] = useState(PLATFORM_CURRENCY);
    const [balanceView, setBalanceView] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    // Back from a redirect-based "add payment method" (setup_intent in the URL), or ?tab=payment-methods (linked from
    // the LLM auto top-up setting) → scroll to the cards section (it is always on this page now)
    useEffect(() => {
        const q = new URLSearchParams(window.location.search);
        if (q.get('setup_intent') || q.get('tab') === 'payment-methods') {
            setTimeout(() => document.getElementById('payment-methods')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
        }
    }, []);
    // Top-ups paid by bank (ACH processing / waiting for a bank transfer): credited once the funds arrive
    const [pendingTopups, setPendingTopups] = useState([]);
    const [topupVisible, setTopupVisible] = useState(false);
    const [topupAmount, setTopupAmount] = useState(100);
    const [topupSubmitting, setTopupSubmitting] = useState(false);

    const handleTopup = async () => {
        if (!topupAmount || topupAmount < 1) {
            message.warning(t('Enter a valid top-up amount'));
            return;
        }
        setTopupSubmitting(true);
        try {
            const result = await billingService.createTopup(topupAmount);
            if (result.success && result.data?.invoice_id) {
                message.success(t('Top-up invoice created. Please complete payment.'));
                navigate(userPath(`/billing/invoices/${result.data.invoice_id}`));
            } else {
                message.error(result.error || t('Failed to create top-up invoice'));
            }
        } catch (error) {
            message.error(error.error || error.message || t('Failed to create top-up invoice'));
        } finally {
            setTopupSubmitting(false);
        }
    };

    useEffect(() => {
        loadBalanceData();
        billingService.getInvoices({ invoice_type: 'topup', status: 'unpaid', page_size: 20 })
            .then((r) => setPendingTopups((r?.data || []).filter((inv) => inv.payment_pending)))
            .catch(() => setPendingTopups([]));
    }, []);

    const loadBalanceData = async () => {
        setLoading(true);
        try {
            // 加载余额
            const balanceResponse = await billingService.getBalance();
            if (balanceResponse.success && balanceResponse.data) {
                setBalance(balanceResponse.data.balance);
                if (balanceResponse.data.currency) setBalanceCurrency(balanceResponse.data.currency);
                setBalanceView(balanceResponse.data);
            }

            // 加载交易记录
            const transactionsResponse = await billingService.getTransactions();
            if (transactionsResponse.success) {
                setTransactions(transactionsResponse.data);
            } else {
                message.error(transactionsResponse.error || t('Failed to load transactions'));
            }
        } catch (error) {
            message.error(t('Failed to load data: {{value}}', { value: error.error || error.message }));
        } finally {
            setLoading(false);
        }
    };

    const getTypeTag = (type) => {
        const typeMap = {
            recharge: { color: 'success', text: t('Top up') },
            deduct: { color: 'error', text: t('Charge') },
            refund: { color: 'processing', text: t('Refund') },
            withdraw: { color: 'warning', text: t('Withdraw') },
            commission: { color: 'success', text: t('Referral commission') }
        };
        const config = typeMap[type] || { color: 'default', text: type };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const columns = [
        {
            title: t('Time'),
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            render: (date) => moment(date).format('YYYY-MM-DD HH:mm:ss')
        },
        {
            title: t('Type'),
            dataIndex: 'type',
            key: 'type',
            width: 100,
            render: (type) => getTypeTag(type)
        },
        {
            title: t('Amount'),
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            render: (amount, record) => {
                const isIncome = ['recharge', 'refund', 'commission'].includes(record.type);
                return (
                    <span style={{ color: isIncome ? '#52c41a' : '#ff4d4f', fontWeight: 500 }}>
                        {isIncome ? '+' : '-'}{formatMoney(Math.abs(Number(amount)), balanceCurrency, { original: true })}
                    </span>
                );
            }
        },
        {
            title: t('Balance before change'),
            dataIndex: 'balance_before',
            key: 'balance_before',
            width: 120,
            render: (b) => formatMoney(b, balanceCurrency, { original: true })
        },
        {
            title: t('Balance after change'),
            dataIndex: 'balance_after',
            key: 'balance_after',
            width: 120,
            render: (b) => formatMoney(b, balanceCurrency, { original: true })
        },
        {
            title: t('Description'),
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (d, record) => (record.type === 'commission' ? t('Referral program') : d),
        },
    ];

    const balanceSection = (
                <>
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col xs={24} sm={12} md={8}>
                            <Card>
                                <Statistic
                                    title={t('Balance')}
                                    valueRender={() => <BalanceMoney view={balanceView || { balance, currency: balanceCurrency }} />}
                                    prefix={<WalletOutlined />}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    style={{ marginTop: 12 }}
                                    onClick={() => setTopupVisible(true)}
                                >
                                    {t('Top up')}
                                </Button>
                            </Card>
                        </Col>
                    </Row>

                    <div id="payment-methods" style={{ marginBottom: 16 }}>
                        <PaymentMethods />
                    </div>

                    <Card
                        title={t('Transactions')}
                        bordered={false}
                        extra={
                            <Button icon={<ReloadOutlined />} onClick={loadBalanceData} loading={loading}>
                                {t('Refresh')}
                            </Button>
                        }
                    >
                        <Table
                            columns={columns}
                            dataSource={transactions}
                            rowKey="id"
                            loading={loading}
                            scroll={{ x: 900 }}
                            locale={{
                                emptyText: (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description={t('No transactions yet')}
                                    />
                                )
                            }}
                            pagination={{
                                showSizeChanger: true,
                                showTotal: (total) => t('{{total}} total', { total })
                            }}
                        />
                    </Card>
                </>
    );

    return (
        <div>
            {pendingTopups.map((inv) => (
                <Alert
                    key={inv.id}
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={inv.payment_pending === 'awaiting_transfer'
                        ? t('Top-up of {{amount}} is waiting for your bank transfer', { amount: formatMoney(inv.total_amount, inv.currency, { original: true }) })
                        : t('Top-up of {{amount}} is processing. Your balance is credited once the funds arrive.', { amount: formatMoney(inv.total_amount, inv.currency, { original: true }) })}
                    action={<Button size="small" onClick={() => navigate(userPath(`/billing/invoices/${inv.id}`))}>{t('View')}</Button>}
                />
            ))}
            {balanceSection}
            <Modal
                title={t('Top up balance')}
                open={topupVisible}
                onCancel={() => setTopupVisible(false)}
                onOk={handleTopup}
                okText={t('Create top-up invoice')}
                cancelText={t('Cancel')}
                confirmLoading={topupSubmitting}
            >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Space wrap>
                        {TOPUP_PRESETS.map((preset) => (
                            <Button
                                key={preset}
                                type={topupAmount === preset ? 'primary' : 'default'}
                                onClick={() => setTopupAmount(preset)}
                            >
                                {formatMoney(preset, balanceCurrency, { decimals: 0, original: true })}
                            </Button>
                        ))}
                    </Space>
                    <InputNumber
                        style={{ width: '100%' }}
                        min={1}
                        max={10000}
                        precision={2}
                        prefix={currencySymbol(balanceCurrency)}
                        value={topupAmount}
                        onChange={setTopupAmount}
                        placeholder={`${t('Custom amount')} (1 ~ 10000 ${balanceCurrency})`}
                    />
                    <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
                        {t('Submitting creates a top-up invoice. Pay online with a bank card or another payment method. Your balance will be credited automatically once payment succeeds.')}
                    </div>
                </Space>
            </Modal>
        </div>
    );
};

export default Balance;
